import { HttpMethods, makeRequest } from '../helpers/http.helper';
import {
  PreOrderRequest,
  UpdatePreOrderRequest,
  PreOrderQueryParams,
  UpdatePreOrderStatusRequest,
  type IncomingPickupPagination,
  type PreOrderDetail,
} from '../types/pre-order.type';

function unwrap<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'result' in response) {
    return (response as { result: T }).result;
  }
  return response as T;
}

// Pre-Orders
export const getPreOrders = (params: PreOrderQueryParams) => {
  return makeRequest({
    url: '/pre_order',
    method: HttpMethods.GET,
    params: params,
  });
};

export const getPreOrder = (preOrderId: string) => {
  return makeRequest({
    url: `/pre_order/${preOrderId}`,
    method: HttpMethods.GET,
  });
};

export const createPreOrder = (preOrder: PreOrderRequest) => {
  return makeRequest({
    url: '/pre_order',
    method: HttpMethods.POST,
    data: preOrder,
  });
};

export const updatePreOrder = (preOrderId: string, preOrder: UpdatePreOrderRequest) => {
  return makeRequest({
    url: `/pre_order/${preOrderId}`,
    method: HttpMethods.PUT,
    data: preOrder,
  });
};

export const updatePreOrderStatus = (preOrderId: string, body: UpdatePreOrderStatusRequest) => {
  return makeRequest({
    url: `/pre_order/${preOrderId}/status`,
    method: HttpMethods.PATCH,
    data: body,
  });
};

export const deletePreOrder = (preOrderId: string) => {
  return makeRequest({
    url: `/pre_order/${preOrderId}`,
    method: HttpMethods.DELETE,
  });
}; 

/** Detail with fulfillment metrics and every linked pickup. */
export const getPreOrderDetail = async (preOrderId: string): Promise<PreOrderDetail> =>
  unwrap<PreOrderDetail>(
    await makeRequest({
      url: `/pre_order/${preOrderId}`,
      method: HttpMethods.GET,
    }),
  );

/** Pickups headed to this recycler that are not attached to any pre-order yet. */
export const listIncomingPickups = async (
  organizationId: string,
  params: { material_category?: string; page?: number; limit?: number } = {},
): Promise<IncomingPickupPagination> =>
  unwrap<IncomingPickupPagination>(
    await makeRequest({
      url: '/pickup_order/incoming',
      method: HttpMethods.GET,
      params: {
        organization_id: organizationId,
        unlinked_only: true,
        page: params.page ?? 1,
        limit: params.limit ?? 50,
        material_category: params.material_category,
      },
    }),
  );

export const linkPreOrderPickups = async (preOrderId: string, pickupOrderIds: string[]): Promise<PreOrderDetail> =>
  unwrap<PreOrderDetail>(
    await makeRequest({
      url: `/pre_order/${preOrderId}/pickups`,
      method: HttpMethods.POST,
      data: { pickup_order_ids: pickupOrderIds },
    }),
  );

export const unlinkPreOrderPickup = async (preOrderId: string, pickupOrderId: string): Promise<PreOrderDetail> =>
  unwrap<PreOrderDetail>(
    await makeRequest({
      url: `/pre_order/${preOrderId}/pickups/${pickupOrderId}`,
      method: HttpMethods.DELETE,
    }),
  );
